import dobrouGmr from "@/utils/dobrouGmr";

const apuracaoTabelaMixin = {
  props: {
    rows:    { type: Array,  required: true },
    search:  { type: String, default: "" },
    gmrInfo: { type: Object, default: null }
  },

  data() {
    return {
      pagina: 1,
      PART_POR_PAGINA: 10
    };
  },

  computed: {
    // Fonte das linhas a paginar. Tabelas específicas (ex.: Chassi) podem
    // sobrescrever `linhasBase` para paginar sobre outra lista.
    linhasBase() {
      return this.rows;
    },

    linhasFiltradas() {
      const termo = (this.search || "").trim().toLowerCase();
      if (!termo) return this.linhasBase;
      const campos = [
        "colaborador", "apelido", "nome_empresa", "funcao",
        "chassi_completo", "chassi_resumido",
        "indicador", "produto", "peca_acessorio", "servico"
      ];
      return this.linhasBase.filter(r =>
        campos.some(c => (r[c] || "").toString().toLowerCase().includes(termo))
      );
    },

    gruposColaborador() {
      return this.agruparPorColaborador(this.linhasFiltradas);
    },

    totalPaginas() {
      return Math.max(1, Math.ceil(this.gruposColaborador.length / this.PART_POR_PAGINA));
    },

    gruposPagina() {
      const ini = (this.pagina - 1) * this.PART_POR_PAGINA;
      return this.gruposColaborador.slice(ini, ini + this.PART_POR_PAGINA);
    }
  },

  watch: {
    // Ao buscar ou trocar os dados, volta para a primeira página.
    search() {
      this.pagina = 1;
    },
    rows() {
      this.pagina = 1;
    }
  },

  methods: {
    mudarPagina(delta) {
      this.pagina = Math.min(this.totalPaginas, Math.max(1, this.pagina + delta));
    },

    agruparPorColaborador(rows) {
      const sorted = [...rows].sort((a, b) => {
        const empA = (a.apelido || a.nome_empresa || "").toUpperCase();
        const empB = (b.apelido || b.nome_empresa || "").toUpperCase();
        if (empA !== empB) return empA.localeCompare(empB, "pt-BR");
        return (a.colaborador || "").localeCompare(b.colaborador || "", "pt-BR");
      });
      const map = new Map();
      for (const row of sorted) {
        const key = row.id_colaborador ?? row.colaborador;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
      }
      return [...map.values()].map(rows => ({ rows }));
    },

    formatarQtd(value) {
      const n = parseFloat(value);
      if (isNaN(n)) return "—";
      return Number.isInteger(n)
        ? n.toLocaleString("pt-BR")
        : n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // base_calculo_indicador e a unidade da metrica: 'Q' quantidade, 'V' valor,
    // 'P' percentual. Nulo fora de conteudo de indicador — cai em quantidade.
    formatarPorBase(base, valor) {
      if (base === "V")
        return this.$options.filters.BrazilianCurrency(valor);
      if (base === "P") return `${this.formatarQtd(valor)}%`;
      return this.formatarQtd(valor);
    },

    formatarValor(row) {
      return this.formatarPorBase(row.base_calculo_indicador, row.valor_meta);
    },

    formatarRealizado(row) {
      return this.formatarPorBase(
        row.base_calculo_indicador,
        row.valor_realizado
      );
    },

    // Indicador por ocorrencia (dias de patio, margem): a meta e 1 por ocorrencia;
    // o qtde_de/qtde_ate e a REGRA (ex.: dias de patio), nao a meta.
    // Detecta pelo tipo_apuracao='O'; se o payload nao trouxer, cai no temFaixa.
    isOcorrencia(row) {
      return (
        String(row.tipo_apuracao || "").trim().toUpperCase() === "O" ||
        this.temFaixa(row)
      );
    },

    // Linha de indicador por ocorrencia (dias de patio): vem por faixa,
    // com qtde_de/qtde_ate preenchidos (indicador normal vem nulo).
    temFaixa(row) {
      return row.qtde_de !== null && row.qtde_de !== undefined && row.qtde_de !== "";
    },

    formatarFaixa(row) {
      const de = this.formatarQtd(row.qtde_de);
      const temAte =
        row.qtde_ate !== null && row.qtde_ate !== undefined && row.qtde_ate !== "";
      if (!temAte || String(row.qtde_de) === String(row.qtde_ate)) return de;
      return `${de}–${this.formatarQtd(row.qtde_ate)}`;
    },

    // Estado da célula "Bônus Pago": cor do valor + chip explicativo.
    // Usa apenas dados já vindos da apuração + o gmrInfo (prop) da campanha.
    statusPagamento(row) {
      const pago = parseFloat(row.valor_bonus_pagamento) || 0;
      const bateu = row.ganhou_meta === "S" || row.ganhou_meta === true;
      const superou = row.superou_gmr === "S" || row.superou_gmr === true;
      const gmr = this.gmrInfo;

      if (!bateu) {
        return {
          valorClass: "grey--text",
          chip: { text: "Não bateu", color: "grey lighten-1", textColor: "white--text" }
        };
      }

      // Percentual no Por Unidade qualifica e nao paga — pago zero com a meta
      // batida e o resultado certo, nao anomalia nem trava de GMR. Quem paga a
      // unidade e o indicador de quantidade da mesma meta.
      const qualificador =
        String(row.base_calculo_indicador || "")
          .trim()
          .toUpperCase() === "P" && Number(row.id_tipo_pagamento) === 1;

      if (qualificador) {
        return {
          valorClass: "grey--text",
          chip: {
            text: "Qualifica, não paga",
            color: "blue-grey lighten-1",
            textColor: "white--text"
          }
        };
      }

      if (pago > 0) {
        const cresc = parseFloat(row.crescimento_gmr);
        const dobrou = !!gmr && superou && dobrouGmr(cresc, gmr.percentual_dobro);
        return {
          valorClass: "success--text font-weight-bold",
          chip: dobrou
            ? { text: `×2 GMR ${cresc}%`, color: "amber darken-1", textColor: "black--text" }
            : null
        };
      }

      // Bateu a meta mas o pagamento veio 0: trava do GMR.
      if (gmr && !superou) {
        return {
          valorClass: "red--text text--darken-1 font-weight-bold",
          chip: { text: "Zerado pelo GMR", color: "red darken-1", textColor: "white--text" }
        };
      }

      // Anomalia (bateu, sem GMR, e mesmo assim 0) — não deveria ocorrer.
      return {
        valorClass: "grey--text",
        chip: { text: "Zerado", color: "grey", textColor: "white--text" }
      };
    }
  }
};

export default apuracaoTabelaMixin;
